/**
 * Micro-Interactions System
 * Provides subtle, delightful micro-interactions that enhance user experience
 */

import { motion } from 'framer-motion';
import { performanceOptimizer } from './performanceOptimizer';
import { getBrowserInfo } from './browserCompatibility';

export interface MicroInteractionConfig {
  type: 'hover' | 'click' | 'focus' | 'scroll' | 'load' | 'success' | 'error';
  intensity: 'subtle' | 'moderate' | 'pronounced';
  duration: number;
  delay?: number;
  easing?: string;
  respectReducedMotion?: boolean;
}

export interface InteractionFeedback {
  visual?: {
    scale?: number;
    rotate?: number;
    brightness?: number;
    blur?: number;
    shadow?: string;
  };
  haptic?: {
    pattern?: number[];
    intensity?: number;
  };
  audio?: {
    frequency?: number;
    duration?: number;
    volume?: number;
  };
}

class MicroInteractionManager {
  private activeInteractions: Map<string, Animation> = new Map();
  private interactionHistory: Array<{
    element: string;
    type: string;
    timestamp: number;
    success: boolean;
  }> = [];

  constructor() {
    this.initializeGlobalInteractions();
  }

  private initializeGlobalInteractions(): void {
    // Add global micro-interactions
    this.addButtonRippleEffect();
    this.addFormFieldFeedback();
    this.addScrollIndicators();
    this.addLoadingMicroAnimations();
    this.addSuccessConfirmations();
  }

  private addButtonRippleEffect(): void {
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      
      if (target.tagName === 'BUTTON' || target.closest('button') || target.classList.contains('btn')) {
        this.createRippleEffect(target, e);
      }
    });
  }

  private createRippleEffect(element: HTMLElement, event: MouseEvent): void {
    if (!performanceOptimizer.shouldUseAnimation('low')) return;

    const rect = element.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;

    const ripple = document.createElement('div');
    ripple.style.cssText = `
      position: absolute;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.6);
      transform: scale(0);
      animation: ripple 0.6s linear;
      left: ${x}px;
      top: ${y}px;
      width: ${size}px;
      height: ${size}px;
      pointer-events: none;
      z-index: 1000;
    `;

    // Ensure parent has relative positioning
    const originalPosition = element.style.position;
    if (!originalPosition || originalPosition === 'static') {
      element.style.position = 'relative';
    }
    element.style.overflow = 'hidden';

    element.appendChild(ripple);

    // Add ripple animation if not exists
    if (!document.querySelector('#ripple-animation')) {
      const style = document.createElement('style');
      style.id = 'ripple-animation';
      style.textContent = `
        @keyframes ripple {
          to {
            transform: scale(4);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);
    }

    // Remove ripple after animation
    setTimeout(() => {
      if (ripple.parentNode) {
        ripple.parentNode.removeChild(ripple);
      }
    }, 600);

    // Add haptic feedback if supported
    this.triggerHapticFeedback('light');
  }

  private addFormFieldFeedback(): void {
    // Focus feedback
    document.addEventListener('focusin', (e) => {
      const target = e.target as HTMLElement;
      
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        this.addFocusGlow(target);
      }
    });

    // Blur feedback
    document.addEventListener('focusout', (e) => {
      const target = e.target as HTMLElement;
      
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        this.removeFocusGlow(target);
      }
    });

    // Validation feedback
    document.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        this.addValidationFeedback(target);
      }
    });
  }

  private addFocusGlow(element: HTMLElement): void {
    if (!performanceOptimizer.shouldUseAnimation('low')) return;

    element.style.transition = 'box-shadow 0.2s ease-out, border-color 0.2s ease-out';
    element.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1), 0 0 20px rgba(59, 130, 246, 0.1)';
    element.style.borderColor = '#3b82f6';
  }

  private removeFocusGlow(element: HTMLElement): void {
    element.style.boxShadow = '';
    element.style.borderColor = '';
  }

  private addValidationFeedback(element: HTMLInputElement): void {
    if (!performanceOptimizer.shouldUseAnimation('low')) return;

    const isValid = element.checkValidity();
    const hasValue = element.value.length > 0;

    if (hasValue) {
      if (isValid) {
        // Success feedback
        element.style.borderColor = '#10b981';
        element.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
        this.addSuccessIcon(element);
      } else {
        // Error feedback
        element.style.borderColor = '#ef4444';
        element.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
        this.addErrorShake(element);
      }
    } else {
      // Reset
      element.style.borderColor = '';
      element.style.boxShadow = '';
      this.removeValidationIcons(element);
    }
  }

  private addSuccessIcon(element: HTMLElement): void {
    this.removeValidationIcons(element);
    
    const icon = document.createElement('div');
    icon.className = 'validation-success-icon';
    icon.innerHTML = '✓';
    icon.style.cssText = `
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%) scale(0);
      color: #10b981;
      font-weight: bold;
      animation: successPop 0.3s ease-out forwards;
      pointer-events: none;
      z-index: 10;
    `;

    const parent = element.parentElement;
    if (parent) {
      parent.style.position = 'relative';
      parent.appendChild(icon);
    }

    // Add animation if not exists
    if (!document.querySelector('#success-pop-animation')) {
      const style = document.createElement('style');
      style.id = 'success-pop-animation';
      style.textContent = `
        @keyframes successPop {
          0% { transform: translateY(-50%) scale(0); }
          50% { transform: translateY(-50%) scale(1.2); }
          100% { transform: translateY(-50%) scale(1); }
        }
      `;
      document.head.appendChild(style);
    }
  }

  private addErrorShake(element: HTMLElement): void {
    if (!performanceOptimizer.shouldUseAnimation('medium')) return;

    element.style.animation = 'errorShake 0.5s ease-in-out';

    // Add animation if not exists
    if (!document.querySelector('#error-shake-animation')) {
      const style = document.createElement('style');
      style.id = 'error-shake-animation';
      style.textContent = `
        @keyframes errorShake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
          20%, 40%, 60%, 80% { transform: translateX(2px); }
        }
      `;
      document.head.appendChild(style);
    }

    // Reset animation
    setTimeout(() => {
      element.style.animation = '';
    }, 500);

    // Haptic feedback for error
    this.triggerHapticFeedback('medium');
  }

  private removeValidationIcons(element: HTMLElement): void {
    const parent = element.parentElement;
    if (parent) {
      const icons = parent.querySelectorAll('.validation-success-icon, .validation-error-icon');
      icons.forEach(icon => icon.remove());
    }
  }

  private addScrollIndicators(): void {
    // Scroll progress indicator
    const progressBar = document.createElement('div');
    progressBar.id = 'scroll-progress';
    progressBar.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 0%;
      height: 3px;
      background: linear-gradient(90deg, #FF5A1F, #f07e56);
      z-index: 9999;
      transition: width 0.1s ease-out;
    `;
    document.body.appendChild(progressBar);

    // Update progress on scroll
    window.addEventListener('scroll', () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      
      progressBar.style.width = `${Math.min(scrollPercent, 100)}%`;
    });

    // Scroll-to-top button
    const scrollButton = document.createElement('button');
    scrollButton.innerHTML = '↑';
    scrollButton.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      width: 50px;
      height: 50px;
      border-radius: 50%;
      background: #3b82f6;
      color: white;
      border: none;
      font-size: 20px;
      cursor: pointer;
      opacity: 0;
      transform: translateY(20px);
      transition: all 0.3s ease;
      z-index: 1000;
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    `;

    scrollButton.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.triggerHapticFeedback('light');
    });

    document.body.appendChild(scrollButton);

    // Show/hide scroll button
    window.addEventListener('scroll', () => {
      if (window.pageYOffset > 300) {
        scrollButton.style.opacity = '1';
        scrollButton.style.transform = 'translateY(0)';
      } else {
        scrollButton.style.opacity = '0';
        scrollButton.style.transform = 'translateY(20px)';
      }
    });
  }

  private addLoadingMicroAnimations(): void {
    // Skeleton loading shimmer
    const style = document.createElement('style');
    style.textContent = `
      .loading-skeleton {
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: shimmer 1.5s infinite;
      }
      
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      
      .pulse-dot {
        animation: pulseDot 1.4s ease-in-out infinite both;
      }
      
      .pulse-dot:nth-child(1) { animation-delay: -0.32s; }
      .pulse-dot:nth-child(2) { animation-delay: -0.16s; }
      
      @keyframes pulseDot {
        0%, 80%, 100% { transform: scale(0); }
        40% { transform: scale(1); }
      }
    `;
    document.head.appendChild(style);
  }

  private addSuccessConfirmations(): void {
    // Listen for form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target as HTMLFormElement;
      
      // Add success animation after form submission
      setTimeout(() => {
        this.showSuccessConfirmation(form);
      }, 100);
    });

    // Listen for successful AJAX requests (if using fetch)
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      
      if (response.ok) {
        this.showGlobalSuccessIndicator();
      }
      
      return response;
    };
  }

  private showSuccessConfirmation(element: HTMLElement): void {
    const confirmation = document.createElement('div');
    confirmation.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #10b981;
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
      z-index: 10000;
      transform: translateX(100%);
      transition: transform 0.3s ease-out;
    `;
    confirmation.innerHTML = '✓ Success!';
    
    document.body.appendChild(confirmation);
    
    // Animate in
    setTimeout(() => {
      confirmation.style.transform = 'translateX(0)';
    }, 10);
    
    // Animate out and remove
    setTimeout(() => {
      confirmation.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (confirmation.parentNode) {
          confirmation.parentNode.removeChild(confirmation);
        }
      }, 300);
    }, 3000);

    // Haptic feedback
    this.triggerHapticFeedback('success');
  }

  private showGlobalSuccessIndicator(): void {
    // Brief green flash at top of screen
    const indicator = document.createElement('div');
    indicator.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: #10b981;
      z-index: 10000;
      transform: scaleX(0);
      transform-origin: left;
      animation: successFlash 0.6s ease-out;
    `;
    
    document.body.appendChild(indicator);
    
    // Add animation
    if (!document.querySelector('#success-flash-animation')) {
      const style = document.createElement('style');
      style.id = 'success-flash-animation';
      style.textContent = `
        @keyframes successFlash {
          0% { transform: scaleX(0); }
          50% { transform: scaleX(1); }
          100% { transform: scaleX(0); }
        }
      `;
      document.head.appendChild(style);
    }
    
    // Remove after animation
    setTimeout(() => {
      if (indicator.parentNode) {
        indicator.parentNode.removeChild(indicator);
      }
    }, 600);
  }

  private triggerHapticFeedback(type: 'light' | 'medium' | 'heavy' | 'success'): void {
    if ('vibrate' in navigator) {
      const patterns = {
        light: [10],
        medium: [20],
        heavy: [30],
        success: [10, 50, 10]
      };
      
      navigator.vibrate(patterns[type]);
    }
  }

  // Public API for custom micro-interactions
  public addHoverEffect(element: HTMLElement, config: MicroInteractionConfig): void {
    if (!performanceOptimizer.shouldUseAnimation(config.intensity === 'subtle' ? 'low' : 'medium')) return;

    const handleMouseEnter = () => {
      const animation = element.animate([
        { transform: 'scale(1)', filter: 'brightness(1)' },
        { transform: 'scale(1.05)', filter: 'brightness(1.1)' }
      ], {
        duration: config.duration,
        easing: config.easing || 'ease-out',
        fill: 'forwards'
      });

      this.activeInteractions.set(`${element.id || 'element'}-hover`, animation);
    };

    const handleMouseLeave = () => {
      const animation = element.animate([
        { transform: 'scale(1.05)', filter: 'brightness(1.1)' },
        { transform: 'scale(1)', filter: 'brightness(1)' }
      ], {
        duration: config.duration * 0.8,
        easing: config.easing || 'ease-out',
        fill: 'forwards'
      });

      this.activeInteractions.set(`${element.id || 'element'}-hover-out`, animation);
    };

    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mouseleave', handleMouseLeave);
  }

  public addClickFeedback(element: HTMLElement, feedback: InteractionFeedback): void {
    element.addEventListener('click', () => {
      if (feedback.visual) {
        this.applyVisualFeedback(element, feedback.visual);
      }
      
      if (feedback.haptic) {
        this.triggerCustomHaptic(feedback.haptic);
      }
      
      if (feedback.audio) {
        this.playAudioFeedback(feedback.audio);
      }
    });
  }

  private applyVisualFeedback(element: HTMLElement, visual: NonNullable<InteractionFeedback['visual']>): void {
    const keyframes: Keyframe[] = [{ offset: 0 }, { offset: 0.5 }, { offset: 1 }];
    
    if (visual.scale) {
      keyframes[0].transform = 'scale(1)';
      keyframes[1].transform = `scale(${visual.scale})`;
      keyframes[2].transform = 'scale(1)';
    }
    
    if (visual.brightness) {
      keyframes[0].filter = 'brightness(1)';
      keyframes[1].filter = `brightness(${visual.brightness})`;
      keyframes[2].filter = 'brightness(1)';
    }

    element.animate(keyframes, {
      duration: 200,
      easing: 'ease-out'
    });
  }

  private triggerCustomHaptic(haptic: NonNullable<InteractionFeedback['haptic']>): void {
    if ('vibrate' in navigator && haptic.pattern) {
      navigator.vibrate(haptic.pattern);
    }
  }

  private playAudioFeedback(audio: NonNullable<InteractionFeedback['audio']>): void {
    // Create audio context for subtle sound feedback
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(audio.frequency || 800, audioContext.currentTime);
      gainNode.gain.setValueAtTime(audio.volume || 0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + (audio.duration || 100) / 1000);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + (audio.duration || 100) / 1000);
    } catch (error) {
      // Audio feedback not supported
    }
  }

  public createLoadingDots(container: HTMLElement): void {
    container.innerHTML = `
      <div style="display: flex; gap: 4px; align-items: center; justify-content: center;">
        <div class="pulse-dot" style="width: 8px; height: 8px; background: currentColor; border-radius: 50%;"></div>
        <div class="pulse-dot" style="width: 8px; height: 8px; background: currentColor; border-radius: 50%;"></div>
        <div class="pulse-dot" style="width: 8px; height: 8px; background: currentColor; border-radius: 50%;"></div>
      </div>
    `;
  }

  public addMagneticEffect(element: HTMLElement, strength: number = 0.3): void {
    if (!performanceOptimizer.shouldUseAnimation('medium')) return;

    let isHovering = false;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isHovering) return;

      const rect = element.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const deltaX = (e.clientX - centerX) * strength;
      const deltaY = (e.clientY - centerY) * strength;
      
      element.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
    };

    const handleMouseEnter = () => {
      isHovering = true;
      element.style.transition = 'transform 0.1s ease-out';
    };

    const handleMouseLeave = () => {
      isHovering = false;
      element.style.transform = 'translate(0, 0)';
      element.style.transition = 'transform 0.3s ease-out';
    };

    document.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mouseleave', handleMouseLeave);
  }

  public cleanup(): void {
    // Cancel all active animations
    this.activeInteractions.forEach(animation => {
      animation.cancel();
    });
    this.activeInteractions.clear();

    // Remove added elements
    const elementsToRemove = [
      '#scroll-progress',
      '.validation-success-icon',
      '.validation-error-icon'
    ];

    elementsToRemove.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => el.remove());
    });
  }
}

// Export singleton instance
export const microInteractions = new MicroInteractionManager();

// Utility functions
export const addHoverEffect = (element: HTMLElement, config: MicroInteractionConfig) =>
  microInteractions.addHoverEffect(element, config);

export const addClickFeedback = (element: HTMLElement, feedback: InteractionFeedback) =>
  microInteractions.addClickFeedback(element, feedback);

export const createLoadingDots = (container: HTMLElement) =>
  microInteractions.createLoadingDots(container);

export const addMagneticEffect = (element: HTMLElement, strength?: number) =>
  microInteractions.addMagneticEffect(element, strength);

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    microInteractions.cleanup();
  });
}